import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { Dialog } from "./radix/Dialog";
import { Poll } from "../types";
import { apiService } from "../services/api";
import "./SearchModal.css";

interface SearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SearchModal: React.FC<SearchModalProps> = ({
  open,
  onOpenChange,
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [polls, setPolls] = useState<Poll[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load polls when debounced search changes
  useEffect(() => {
    if (debouncedSearchQuery.trim()) {
      loadPolls();
    } else {
      setPolls([]);
    }
  }, [debouncedSearchQuery]);

  // Reset search when modal closes
  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setPolls([]);
      setError(null);
    }
  }, [open]);

  const loadPolls = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.listPolls(
        20,
        undefined,
        debouncedSearchQuery,
      );
      setPolls(response.polls);
    } catch (err: any) {
      setError(err.message || "Failed to search polls");
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearchQuery]);

  const handlePollClick = (pollId: string) => {
    navigate(`/feed/${pollId}`);
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Search Polls"
      className="search-modal"
    >
      <div className="search-modal-content">
        <div className="search-input-container">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder="Search polls..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-modal-input"
            autoFocus
          />
          {searchQuery && (
            <button
              className="clear-btn"
              onClick={() => setSearchQuery("")}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}

        {isLoading ? (
          <div className="search-loading">Searching...</div>
        ) : searchQuery && polls.length === 0 ? (
          <div className="search-empty">
            <p>No polls found for "{searchQuery}"</p>
            <p className="search-hint">Try a different search term</p>
          </div>
        ) : polls.length > 0 ? (
          <div className="search-results">
            {polls.map((poll) => (
              <button
                key={poll.pollId}
                className="search-result-item"
                onClick={() => handlePollClick(poll.pollId)}
              >
                <div className="search-result-question">{poll.question}</div>
                <div className="search-result-meta">
                  {poll.totalVotes} vote{poll.totalVotes !== 1 ? "s" : ""} •{" "}
                  {poll.options.length} options
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="search-empty">
            <p>Start typing to search polls</p>
          </div>
        )}
      </div>
    </Dialog>
  );
};
